import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import { initialState, PaymentMethod } from "./paymentMethods-state";
import {
  deletePaymentMethods,
  fetchPaymentMethods,
  savePaymentMethod,
} from "./paymentMethods-action";
import { RootState } from "store";
import { parseExpirationDate } from "utility";

const paymentMethodEntityAdapter = createEntityAdapter<PaymentMethod>({});

export const paymentMethodsSlice = createSlice({
  name: "paymentMethods",
  initialState: paymentMethodEntityAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      fetchPaymentMethods.fulfilled,
      (state, action: PayloadAction<PaymentMethod[]>) => {
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        paymentMethodEntityAdapter.setAll(state, action.payload);
      }
    );
    builder.addCase(fetchPaymentMethods.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(fetchPaymentMethods.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
    /* Save method cases */
    builder.addCase(
      savePaymentMethod.fulfilled,
      (state, action: PayloadAction<PaymentMethod>) => {
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        paymentMethodEntityAdapter.upsertOne(state, action.payload);
      }
    );
    builder.addCase(savePaymentMethod.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(savePaymentMethod.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
    /* Delete Method cases */
    builder.addCase(
      deletePaymentMethods.fulfilled,
      (state, action: PayloadAction<any>) => {
        state.meta.isLoading = false;
        state.meta.isSuccess = true;
        state.meta.status = "idle";
        paymentMethodEntityAdapter.removeOne(state, action.payload);
      }
    );
    builder.addCase(deletePaymentMethods.pending, (state) => {
      state.meta.isLoading = true;
      state.meta.status = "inProcess";
    });
    builder.addCase(deletePaymentMethods.rejected, (state) => {
      state.meta.isLoading = false;
      state.meta.status = "idle";
      state.meta.error = "true";
    });
  },
});

export const {
  selectAll: selectAllPaymentMethods,
  selectById: selectPaymentMethodById,
} = paymentMethodEntityAdapter.getSelectors(
  (state: RootState) => state.paymentMethods
);

export const selectExpiredCardPaymentMethods = createSelector([selectAllPaymentMethods], (paymentMethods: PaymentMethod[]): PaymentMethod[] => {
  const currentDate = new Date();
  return paymentMethods.filter((method) => {
    if (!method.expirationDate) {
      return false;
    }
    const expirationDate = parseExpirationDate(method.expirationDate);
    if (!expirationDate) {
      return false;
    }
    return expirationDate < currentDate;
  });
});

export const selectExpiringNextMonthCardPaymentMethods = createSelector([selectAllPaymentMethods], (paymentMethods: PaymentMethod[]): PaymentMethod[] => {
  const currentDate = new Date();
  const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() , 1);
  const endOfNextMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 2, 0, 23, 59, 59, 999);

  return paymentMethods.filter((method) => {
    if (!method.expirationDate) {
      return false;
    }
    const expirationDate = parseExpirationDate(method.expirationDate);
    if (!expirationDate) {
      return false;
    }
    return expirationDate >= currentMonth && expirationDate <= endOfNextMonth;
  });
});

export default paymentMethodsSlice.reducer;
